// Schiff: Bewegung, Energiehaushalt, Waffensysteme.
//
// Der Laser feuert **selbsttaetig** auf alles in Reichweite. Es gibt keinen
// Feuerknopf und keine Zielauswahl — aktives Zielen wuerde aus einem
// Inkrementalspiel einen Shooter machen (GAME-001).
//
// **Energie ist Reibung, nicht Bestrafung** (Gate FG-29): leer bedeutet
// langsamer und seltener, nie Stillstand. In der Ladezone ist Feuern gratis.
//
// Das Modul kennt weder Phaser noch die Oberflaeche. Treffer und Abschuesse
// meldet es ueber Rueckrufe — dieselbe Entkopplung, die frueher die
// Effektschicht testbar hielt.

import { balancing } from '../data/balancing';
import type { DerivedStats } from '../data/types';
import { clamp, turnTowards } from '../core/math';
import { newWarpState, warpAllowed, warpStep, type WarpState } from './navigation';
import { chance, rnd } from '../core/rng';
import type { AnomalyEffect } from './anomalies';
import type { Rock } from './field';
import { enemyDamage, isColossal, slotsFor, type Hostile } from './hostiles';
import {
  assignDroneTargets, droneArrived, fitDrones, flyDrones, type Drone,
} from './drones';
import type { World } from './world';

export interface ShipInput {
  ax: number;
  ay: number;
  boost: boolean;
  /**
   * Der Lichtflug ist **angefordert** — ob er auch steht, entscheidet das
   * Schiff (Anlauf, Energie, Gefecht). Ein Wunsch, kein Befehl: dieselbe Form
   * wie `boost`, und aus demselben Grund.
   */
  warp?: boolean;
}


/** Geteilte leere Liste — kein neues Array je Frame, wenn es keine Gegner gibt. */
const NO_HOSTILES: Hostile[] = [];

/**
 * Was das Schiff von den Gegnern braucht — die Zielabfrage, mehr nicht.
 *
 * Bewusst schmal statt `HostileField` selbst: das Schiff soll Gegner
 * **beschiessen** koennen, ohne ihren Zustand zu kennen oder zu schreiben.
 * `HostileField` erfuellt die Form von sich aus.
 */
export interface HostileTargets {
  inRange(x: number, y: number, radius: number, out: Hostile[]): Hostile[];
  /**
   * Gegner in einem Kreis — **ohne** den Wachbereich, reine Geometrie.
   *
   * Die Kette braucht diese Fassung: `inRange` traegt die Symmetrie „wer
   * schiessen kann, kann getroffen werden", und die gilt vom Schiff aus, nicht
   * von einem wandernden Kettenpunkt.
   */
  inCircle(x: number, y: number, radius: number, out: Hostile[]): Hostile[];
  /**
   * Gegner, in deren Wachbereich das Schiff **steht** — die, die feuern.
   *
   * Der Lichtflug liest sie: wer beschossen wird, springt nicht davon. Sie
   * steht hier und nicht nur in der Szene, weil sie eine **Regel** trägt und
   * nicht nur eine Anzeige.
   */
  engaging(x: number, y: number, out: Hostile[]): Hostile[];
  byId(id: string): Hostile | undefined;
  alive(hostile: Hostile): boolean;
}

/** Was das Schiff nach aussen meldet. Es entscheidet nichts selbst. */
/**
 * Woher ein Kettensprung ausgeht — Brocken **oder** Gegner.
 *
 * Nur das, was die Kette davon braucht: eine ID für die Sperre und einen Ort
 * für den nächsten Sprung. Dieselbe Sparsamkeit wie bei `GuidedTarget`, und aus
 * demselben Grund — ein Erz hat ein Gegner nicht.
 */
export interface ChainFrom {
  id: string;
  x: number;
  y: number;
}

export interface ShipHooks {
  /**
   * Ein Waffenzyklus hat ausgeloest — **einmal je Zyklus**, nicht je Ziel.
   *
   * Der Zaehler liegt hier und nicht bei `onLaser`, weil ein Mehrfachlaser je
   * Zyklus mehrere Ziele trifft: „abgegebene Schuesse" waere sonst „getroffene
   * Brocken". `sim/` darf `state/` nicht kennen (Schichten-Contract FG-04),
   * darum ein Rueckruf und kein Zugriff.
   */
  onFire(channel: string, shots: number): void;
  onLaser(from: { x: number; y: number }, rock: Rock, damage: number, crit: boolean): void;
  onChain(from: Rock, to: Rock, damage: number, crit: boolean): void;
  /**
   * Ein Kettensprung, der einen **Gegner** trifft.
   *
   * Ein eigener Haken und kein Zweig im bestehenden: welche Art getroffen wird,
   * weiss der Aufrufer daran, in **welcher Liste** er das Ziel gefunden hat —
   * eine Unterscheidung ueber das ID-Praefix waere die stille Typunterscheidung,
   * gegen die auch `sim/guided.ts` gebaut ist.
   *
   * Der Schaden kommt bereits mit dem Gefechtszuschlag; er gilt fuer den
   * Gegneranteil und nur dort.
   */
  onChainHostile(from: ChainFrom, to: Hostile, damage: number): void;
  onMissile(x: number, y: number, angle: number, damage: number, splash: number): void;
  onNova(x: number, y: number, radius: number, damage: number): void;
  onDrone(drone: Drone, rock: Rock, damage: number): void;
  /**
   * Dieselben Waffen, aber auf einen Gegner.
   *
   * Bewusst **eigene** Haken statt `onLaser` auf eine Vereinigung zu weiten:
   * der Aufrufer muss den Unterschied sehen (ein Brocken bucht Erz, ein Gegner
   * Erz **und** Erfahrung), und ein Zweig ueber das ID-Praefix `e:` waere eine
   * stille Typunterscheidung.
   */
  onHostileLaser(
    from: { x: number; y: number }, hostile: Hostile, damage: number, crit: boolean,
  ): void;
  onHostileDrone(drone: Drone, hostile: Hostile, damage: number): void;
}

export interface ShipContext {
  world: World;
  stats: DerivedStats;
  /** Energie in `[0, energyMax]`. Das Schiff schreibt sie fort. */
  energy: number;
  /** Erze, die uebersprungen werden sollen. Nie alle. */
  ignoredOres?: Record<string, boolean>;
  /**
   * Das **Wunscherz** der Drohnen — leer heißt „keins".
   *
   * Eine Neigung, keine Zielauswahl: es wirkt **nur** auf den Drohnenkanal, und
   * ist keins in Reichweite, graben sie die nächstbesten weiter.
   */
  wishOre?: string;
  /**
   * Die Anomalie am Schiffsort (`sim/anomalies.ts`) — optional, neutral wenn
   * sie fehlt. Nur Reibung oder Belohnung: `dragMul >= 1`, `fireFree` wirkt
   * wie die Ladezone, `regen` laedt auch draussen. Der Server rechnet dieselbe
   * Blase selbst aus.
   */
  anomaly?: AnomalyEffect;
  /**
   * Energie je Sekunde aus **gezuendeten Boostern** — der dritte Weg zur
   * vollen Zelle draussen, nach Ort (Solarwind-Front) und Handlung (erledigter
   * Gegner). Er kommt als Zahl herein und nicht als Spielstand: das Schiff
   * kennt keinen (Gate FG-26), und der Aufrufer weiss ohnehin, welche laufen.
   */
  regen?: number;
  /** Die Gegner im Feld — optional, damit `Ship` ohne sie testbar bleibt. */
  hostiles?: HostileTargets;
  /**
   * Die Notabschaltung läuft: keine Eingabe, kein Feuer.
   *
   * Sie kostet **Zeit**, nie Fortschritt (`GAME-001`). Die Energie lädt
   * weiter, weil das Schiff an der Kapsel steht — das ist kein Fortschritt,
   * sondern die Ladezone. Der Server rechnet denselben Schritt
   * (`mine_tick(towing=…)`), sonst wäre die Zusage online eine andere als solo.
   */
  frozen?: boolean;
  hooks: ShipHooks;
}

/** Geteilte Nulleingabe — kein neues Objekt je Frame während der Notabschaltung. */
const NO_INPUT: ShipInput = { ax: 0, ay: 0, boost: false, warp: false };

export class Ship {
  x = 0;
  y = 0;
  vx = 0;
  vy = 0;
  angle = -Math.PI / 2;
  thrust = 0;
  boosting = false;
  docked = false;
  empty = false;
  rangePulse = 0;
  /**
   * Steht ein Gegner im Wirkbereich der Flächenwaffen?
   *
   * Dann bekommen **Brocken** dort keinen Schaden mehr: der Abbau pausiert im
   * Gefecht (`_targetable` / `area_locked` auf der Serverseite). Der Einschlag
   * fällt in der Szene, deshalb ist es ein gelesenes Feld und kein Rückruf.
   */
  areaEngaged = false;
  /**
   * Steht das Sprungfeld? Reine Anzeige — die Wirkung steckt in `warp`.
   *
   * Solange es steht, schweigen die Waffen: reisen oder abbauen, nicht beides.
   */
  warping = false;
  /**
   * Steht ein Gegner im Wachbereich?
   *
   * Der Lichtflug liest es: wer beschossen wird, springt nicht davon. Es wird
   * im Waffenschritt gesetzt und ist damit **einen Frame alt** — das genügt
   * für eine Entscheidung, die Sekunden dauert.
   */
  inCombat = false;
  /** Der Zustand des Sprungfelds. Er gehört dem Schiff, nicht dem Spielstand. */
  readonly warp: WarpState = newWarpState();
  readonly drones: Drone[] = [];

  private laserT = 0;
  private missileT = 0;
  private novaT = 0;
  /** Wiederverwendete Zielpuffer — kein neues Array je Schuss (Gate FG-14). */
  private readonly near: Rock[] = [];
  private readonly chainNear: Rock[] = [];
  /** Eigener Puffer der Kette — `hostileNear` gehoert Laser und Drohnen. */
  private readonly chainFoes: Hostile[] = [];
  private readonly hit = new Set<string>();
  /**
   * Eigener Zielpuffer der Drohnen (Gate FG-14).
   *
   * Bewusst **nicht** `near`: der Puffer muss ueber die ganze Drohnenschleife
   * stabil bleiben, und `targets()` wuerde ihn je Drohne neu fuellen.
   */
  private readonly droneNear: Rock[] = [];
  /** Gefilterte Drohnenziele — wiederverwendet, kein neues Array je Schritt. */
  private readonly droneFree: Rock[] = [];
  /** Wer gerade feuert — eigener Puffer, damit er die Zielsuche nicht stört. */
  private readonly engagedNear: Hostile[] = [];
  /** Zielpuffer der Gegner — eigener Puffer, weil `near` die Brocken haelt. */
  private readonly hostileNear: Hostile[] = [];
  /** Eigener Puffer der Flaechensperre — `hostileNear` gehoert Laser und Drohnen. */
  private readonly areaNear: Hostile[] = [];
  /** Schon vergebene Ziel-IDs dieses Schritts. Wird geleert, nie neu erzeugt. */
  private readonly claimed = new Set<string>();
  /**
   * Wie viele Drohnen ein Gegner noch braucht. Wird geleert, nie neu erzeugt.
   *
   * Brocken vertragen `claimed` — eine Drohne je Brocken, sonst verschenken
   * zwei ihren Schuss aneinander. Gegner brauchen das Gegenteil: sie sollen
   * **schnellstens** fallen, also duerfen mehrere Drohnen auf denselben. Ein
   * Zaehler statt eines Hakens ist der ganze Unterschied.
   */
  private readonly hostileNeed = new Map<string, number>();
  /** Aufteilung der Laser-Zielplaetze auf die Gegner. Wird geleert, nie neu erzeugt. */
  private readonly laserSlots = new Map<string, number>();

  reset(x = 0, y = 0): void {
    this.x = x; this.y = y;
    this.vx = 0; this.vy = 0;
    this.laserT = 0; this.missileT = 0; this.novaT = 0;
    this.drones.length = 0;
  }

  get distToBase(): number {
    return Math.hypot(this.x, this.y);
  }

  /** Ein Simulationsschritt. Gibt die fortgeschriebene Energie zurueck. */
  update(dt: number, rawInput: ShipInput, ctx: ShipContext): number {
    const data = balancing();
    const v = ctx.stats.values;
    let energy = ctx.energy;
    const frozen = ctx.frozen === true;
    const input = frozen ? NO_INPUT : rawInput;

    // ── Energie ──────────────────────────────────────────────────────────────
    this.docked = this.distToBase < data.dockRange;
    if (this.docked) {
      energy = Math.min(v.energyMax, energy + v.dockRegen * dt);
    } else {
      energy = Math.max(0, energy - v.energyIdle * v.energyEff * dt);
      // Die Solarwind-Front laedt auch hier draussen — deutlich langsamer als
      // die Kapsel, aber sie macht die Reichweite zu einer Entscheidung statt
      // zu einer festen Grenze. Wortgleich in `combat.tick_energy`.
      // Und der gekaufte Booster **dazu**, nicht statt: wer im Sturm steht und
      // ein Sonnensegel gezuendet hat, laedt beides. Wortgleich zum Server, der
      // `effect.regen + booster_regen(...)` hereinreicht.
      const regen = (ctx.anomaly?.regen ?? 0) + (ctx.regen ?? 0);
      if (regen > 0) energy = Math.min(v.energyMax, energy + regen * dt);
    }
    this.empty = energy <= 0.01;
    const speedFactor = this.empty ? data.emptySpeed : 1;

    // ── Bewegung ─────────────────────────────────────────────────────────────
    let ax = input.ax;
    let ay = input.ay;
    const len = Math.hypot(ax, ay);
    if (len > 1) { ax /= len; ay /= len; }
    this.thrust = clamp(this.thrust + (len > 0.05 ? 4 : -6) * dt, 0, 1);

    this.inCombat = ctx.hostiles
      ? ctx.hostiles.engaging(this.x, this.y, this.engagedNear).length > 0
      : false;

    // ── Lichtflug ────────────────────────────────────────────────────────────
    // Er ist **kein** zweiter Boost, sondern ein eigener Zustand mit Anlauf:
    // solange er steht, tragen die Waffen nicht. Reisen oder abbauen — nicht
    // beides. Genau deshalb ist er auch kein Balancing-Problem: er bringt kein
    // Erz, er spart Zeit.
    const warp = this.stepWarp(dt, input, ctx, energy, frozen);
    energy = Math.max(0, energy - warp.drain * v.energyEff * dt);

    const wantBoost = input.boost && len > 0.05 && energy > 4 && !this.empty;
    this.boosting = wantBoost;
    const boost = Math.max(wantBoost ? v.boostMul : 1, warp.mul);
    if (wantBoost) energy = Math.max(0, energy - v.boostDrain * v.energyEff * dt);

    this.vx += ax * v.accel * speedFactor * boost * dt;
    this.vy += ay * v.accel * speedFactor * boost * dt;

    // Der Ionensturm bremst — nie umgekehrt: ein Faktor unter 1 liesse die
    // Bewegungspruefung des Servers ehrliche Meldungen still verwerfen.
    const drag = Math.exp(-v.drag * (ctx.anomaly?.dragMul ?? 1) * dt);
    this.vx *= drag; this.vy *= drag;

    const speed = Math.hypot(this.vx, this.vy);
    const maxSpeed = v.maxSpeed * speedFactor * boost;
    if (speed > maxSpeed) {
      this.vx = (this.vx / speed) * maxSpeed;
      this.vy = (this.vy / speed) * maxSpeed;
    }

    this.applyWall(dt);

    this.x += this.vx * dt;
    this.y += this.vy * dt;
    if (speed > 12) this.angle = turnTowards(this.angle, Math.atan2(this.vy, this.vx), 9 * dt);
    this.rangePulse += dt;

    // ── Waffen ───────────────────────────────────────────────────────────────
    // Im Lichtflug schweigen sie. Ein Sprungfeld, das nebenbei abbaut, wäre
    // eine Abkürzung statt einer Reise — und der Server rechnet dasselbe: dort
    // steht das Schiff schlicht nie lange genug an einem Brocken.
    return frozen || this.warping ? energy : this.fireAll(dt, ctx, energy);
  }

  /**
   * Das Sprungfeld fortschreiben — der Zustand gehoert dem Schiff.
   *
   * Er ist **kein** zweiter Boost: der Anlauf kostet Sekunden, in denen man
   * verwundbar bleibt, und solange das Feld steht, schweigen die Waffen.
   */
  private stepWarp(
    dt: number, input: ShipInput, ctx: ShipContext, energy: number, frozen: boolean,
  ): { mul: number; drain: number } {
    const wish = input.warp === true && warpAllowed(ctx.stats, energy, this.inCombat, frozen);
    const step = warpStep(this.warp, dt, wish, ctx.stats);
    this.warping = this.warp.mode === 'on';
    return step;
  }

  /** Naechste Brocken im Umkreis, sortiert nach Distanz. */
  /**
   * Die naechsten Brocken in Reichweite.
   *
   * Der **Erzfilter** wirkt hier: ausgeschaltete Erze werden uebersprungen. Er
   * kann nie alle ausschliessen, also bleibt immer etwas uebrig.
   */
  private targets(ctx: ShipContext, x: number, y: number, range: number, max: number): Rock[] {
    // Haben die Gegner alle Plaetze belegt, faellt die Abfrage ganz weg.
    if (max <= 0) { this.near.length = 0; return this.near; }
    const roh = ctx.world.queryCircle(x, y, range, this.near);
    const aus = ctx.ignoredOres;
    const found = aus ? roh.filter((rock) => !aus[rock.ore]) : roh;
    if (found.length > 1) {
      found.sort(
        (a, b) => (a.x - x) ** 2 + (a.y - y) ** 2 - ((b.x - x) ** 2 + (b.y - y) ** 2),
      );
    }
    return found.length > max ? found.slice(0, max) : found;
  }

  /**
   * Energie abziehen. Bei Ebbe feuern die Waffen **langsamer**, nicht gar nicht,
   * und an der Basis ist Strom umsonst.
   */
  private spend(ctx: ShipContext, energy: number, cost: number): { energy: number; rate: number } {
    // Die Nullfeld-Tasche wirkt wie die Ladezone: Feuern ist gratis. Das
    // Energieversprechen (FG-29) wird dadurch nur grosszuegiger, nie strenger.
    if (this.docked || ctx.anomaly?.fireFree) return { energy, rate: 1 };
    const need = cost * ctx.stats.values.energyEff;
    if (energy >= need) return { energy: energy - need, rate: 1 };
    return { energy: 0, rate: balancing().emptyRate };
  }

  /**
   * Die naechsten Gegner in Reichweite — **vor** den Brocken.
   *
   * Kuerzt den wiederverwendeten Puffer an Ort und Stelle, statt zu schneiden:
   * `slice` waere ein neues Array je Schuss (Gate FG-14).
   */
  private hostileTargets(ctx: ShipContext, range: number, max: number): Hostile[] {
    const out = this.hostileNear;
    if (!ctx.hostiles || max <= 0) { out.length = 0; return out; }
    ctx.hostiles.inRange(this.x, this.y, range, out);
    if (out.length > max) out.length = max;
    return out;
  }

  /**
   * Die Zielplaetze des Lasers auf die Gegner verteilen — **gebuendelt**.
   *
   * Der naechste Gegner bekommt so viele Plaetze, wie er braucht; erst der
   * Ueberschuss geht an den naechsten und danach an die Brocken. Ein Platz je
   * Gegner waere gestreutes Feuer: alle ueberleben lang, und alle schiessen die
   * ganze Zeit weiter. Wortgleich zu `_aim_hostiles` auf dem Server.
   *
   * Schreibt die Aufteilung in den wiederverwendeten `laserSlots` und gibt die
   * Summe zurueck — getrennt vom Feuern, damit die Schuesse erst nach der
   * Energieabrechnung fallen, so wie vor der Buendelung.
   */
  private aimHostiles(feinde: Hostile[], shot: number, max: number): number {
    this.laserSlots.clear();
    let used = 0;
    for (const hostile of feinde) {
      const slots = slotsFor(hostile.hp, shot, max - used);
      if (slots <= 0) continue;
      this.laserSlots.set(hostile.id, slots);
      used += slots;
      if (used >= max) break;
    }
    return used;
  }

  /**
   * Alle Waffenkanäle eines Schritts.
   *
   * Ausgelagert, damit `update` unter der Strukturgrenze bleibt (FG-06) — und
   * weil es genau **eine** Stelle geben soll, an der das Feuern aussetzt.
   */
  private fireAll(dt: number, ctx: ShipContext, energy: number): number {
    const v = ctx.stats.values;
    // Der Wirkbereich der Flächenwaffen — dieselbe Zahl wie `area_range` auf
    // dem Server: Raketen reichen 2,2× Laser, die Nova ihren eigenen Radius.
    const areaRange = Math.max(v.laserRange * 2.2, ctx.stats.unlocks.nova ? v.novaRadius : 0);
    this.areaEngaged = ctx.hostiles
      ? ctx.hostiles.inRange(this.x, this.y, areaRange, this.areaNear).length > 0
      : false;
    energy = this.fireLaser(dt, ctx, energy);
    if (ctx.stats.unlocks.missile) energy = this.fireMissiles(dt, ctx, energy);
    if (ctx.stats.unlocks.nova && v.novaRate > 0) this.fireNova(dt, ctx);
    if (ctx.stats.unlocks.drone && v.droneCount > 0) this.updateDrones(dt, ctx);
    return energy;
  }

  private fireLaser(dt: number, ctx: ShipContext, energy: number): number {
    const v = ctx.stats.values;
    this.laserT -= dt;
    if (this.laserT > 0) return energy;

    // **Gegner zuerst, gebuendelt — und der Abbau pausiert dabei.** Sie belegen
    // die Zielplaetze vor den Brocken, so viele, wie der naechste braucht, nicht
    // einen je Gegner. Nicht benoetigte Plaetze **verfallen**, statt ins Erz
    // zurueckzufallen: sonst hiesse „Vorrang" nur „zuerst", und der Ueberschuss
    // gruebe weiter, waehrend die Huelle faellt. Es gibt weiter keinen
    // Feuerknopf und keine Zielauswahl (`GAME-001`) — nur eine Rangfolge.
    const max = Math.round(v.laserTargets);
    const shot = enemyDamage(v.laserDamage * v.globalDamage);
    const feinde = this.hostileTargets(ctx, v.laserRange, max);
    const belegt = this.aimHostiles(feinde, shot, max);
    const list = this.targets(ctx, this.x, this.y, v.laserRange, feinde.length ? 0 : max - belegt);
    if (!belegt && !list.length) { this.laserT = 0.05; return energy; }

    const spent = this.spend(ctx, energy, v.laserCost);
    this.laserT = 1 / (v.laserRate * spent.rate);
    ctx.hooks.onFire('laser', belegt + list.length);

    // Ohne Kopie: ein erledigter Gegner verschwindet nicht aus der Liste, er
    // faellt nur auf null Huellenpunkte. Anders als beim Brocken unten mutiert
    // hier also niemand, worueber gerade gelaufen wird.
    for (const hostile of feinde) {
      const slots = this.laserSlots.get(hostile.id) ?? 0;
      for (let i = 0; i < slots; i++) {
        const crit = chance(v.critChance);
        ctx.hooks.onHostileLaser(this, hostile, shot * (crit ? v.critMul : 1), crit);
        // **Ein Treffer auf einen Gegner springt genauso weiter.** Er tat es
        // nicht: der Sprung stand nur in der Brocken-Schleife darunter. Der
        // Grundschaden ist der **unverstärkte** — den Zuschlag legt die Kette
        // erst am jeweiligen Gegner auf, sonst bekäme ihn ein Brocken dahinter.
        if (v.bounce > 0) {
          const grund = v.laserDamage * v.globalDamage * (crit ? v.critMul : 1);
          this.chain(
            ctx, hostile, grund * v.bounceFalloff, Math.round(v.bounce), crit, true,
            isColossal(hostile.kindId),
          );
        }
      }
    }
    for (const rock of [...list]) {
      const crit = chance(v.critChance);
      const damage = v.laserDamage * v.globalDamage * (crit ? v.critMul : 1);
      ctx.hooks.onLaser(this, rock, damage, crit);
      if (v.bounce > 0) {
        this.chain(ctx, rock, damage * v.bounceFalloff, Math.round(v.bounce), crit, false);
      }
    }
    return spent.energy;
  }

  /**
   * Kettenreaktion: der Strahl springt zum jeweils naechsten unberuehrten Ziel.
   *
   * **Der Sprung bleibt bei seiner Art.** Ein Treffer auf einen Gegner springt
   * zu Gegnern, ein Treffer auf einen Brocken zu Brocken. Das ist keine
   * Vereinfachung, sondern die Regel „**der Abbau pausiert im Gefecht**"
   * (`GAME-001`): die gemischte Fassung liess den Sprung vom Gegner auf den
   * naechstliegenden Brocken los und grub damit weiter, waehrend die Huelle
   * fiel — durch die Hintertuer genau das, was `feinde.length ? 0 : …` oben
   * verhindert. Der Server rechnet es ebenso: steht ein Gegner im Fokus, ist
   * `focus_locked` gesetzt, und die Brocken bekommen aus diesem Kanal nichts.
   *
   * Sie ist ausserdem die Richtung, in die das Ticket zeigte: bis hierher
   * sprang ein Treffer auf einen Gegner **gar nicht**, waehrend der Server den
   * Bounce-Zuschlag in `focus_dps` voll gegen ihn buchte.
   *
   * `damage` ist der **unverstaerkte** Grundschaden. Der Gefechtszuschlag kommt
   * erst am Gegner dazu; laege er schon hier drin, bekaeme ein Brocken ihn mit.
   *
   * Die Sperre `hit` gilt trotzdem ueber beide Arten — sie kostet nichts, und
   * die ID-Raeume sind ohnehin verschieden (`cx:cy:i` gegen `e:`/`g:`).
   *
   * `selbstziel` ist die eine Ausnahme von der Sperre: **ein kolossaler Gegner
   * ist sein eigener Kettenpartner.** Ein Koerper mit `r = 72` gegen eine
   * Sprungweite von 130 ist die Sprungflaeche selbst — der Strahl braucht
   * neben ihm keinen Nachbarn, er hat noch Ziel genug vor sich. Wortgleiches
   * Gegenstueck zum Zweig in `_has_chain_partner`; ohne ihn haengt der
   * Kettenanteil (**92 %** des Fokusschadens) daran, ob zufaellig eine
   * Schrottdrohne nachgerueckt ist.
   */
  private chain(
    ctx: ShipContext, from: ChainFrom, damage: number, jumps: number, crit: boolean,
    foes: boolean, selbstziel = false,
  ): void {
    const v = ctx.stats.values;
    this.hit.clear();
    if (!selbstziel) this.hit.add(from.id);
    let current: ChainFrom = from;
    for (let i = 0; i < jumps; i++) {
      // **Reine Geometrie, auch bei den Gegnern.** `inRange` nimmt zusaetzlich
      // jeden Gegner auf, dessen Wachbereich den Abfragepunkt deckt — richtig
      // vom Schiff aus, wo es die Symmetrie „wer schiessen kann, kann getroffen
      // werden" traegt, falsch von einem wandernden Kettenpunkt, an dem niemand
      // steht: mit `reach` bis 900 sprang die Kette siebenmal weiter als ihre
      // Sprungweite, und der Server buchte dort nichts.
      const next = foes
        ? (ctx.hostiles
          ? this.nearestFree(
            ctx.hostiles.inCircle(current.x, current.y, v.bounceRange, this.chainFoes), current,
          )
          : null)
        : this.nearestFree(
          ctx.world.queryCircle(current.x, current.y, v.bounceRange, this.chainNear), current,
        );
      if (!next) break;

      if (foes) {
        ctx.hooks.onChainHostile(current, next as Hostile, enemyDamage(damage));
      } else {
        ctx.hooks.onChain(current as Rock, next as Rock, damage, crit);
      }
      ctx.hooks.onFire('bounce', 1);
      // Beim Selbstziel bleibt die Sperre offen: der naechste Sprung soll
      // denselben Koerper wieder treffen, sonst faende er ueberhaupt kein Ziel
      // mehr und die Kette braeche nach dem ersten Sprung ab.
      if (!selbstziel) this.hit.add(next.id);
      current = next;
      damage *= v.bounceFalloff;
      if (damage < 0.01) break;
    }
  }

  /**
   * Das naechste noch unberuehrte Ziel einer Liste.
   *
   * Gibt ein **Element der uebergebenen Liste** zurueck, kein neues Objekt: sie
   * laeuft je Sprung, und bei Vollausbau sind das ein paar hundert je Sekunde
   * (Gate FG-14). Die Zwischenfassung reichte hier ein `{ target, dist }`
   * heraus, weil der Aufrufer zwei Listen vergleichen musste — mit der
   * artreinen Kette gibt es nur noch eine, und der Abstand wird nicht mehr
   * gebraucht.
   */
  private nearestFree<T extends { id: string; x: number; y: number }>(
    list: readonly T[], from: { x: number; y: number },
  ): T | null {
    let best: T | null = null;
    let bestDist = Infinity;
    for (const target of list) {
      if (this.hit.has(target.id)) continue;
      const d = (target.x - from.x) ** 2 + (target.y - from.y) ** 2;
      if (d < bestDist) { bestDist = d; best = target; }
    }
    return best;
  }

  private fireMissiles(dt: number, ctx: ShipContext, energy: number): number {
    const v = ctx.stats.values;
    this.missileT -= dt;
    if (this.missileT > 0) return energy;

    // Nur eine Torfrage „ist da ueberhaupt etwas?" — die Rakete sucht sich ihr
    // Ziel danach selbst. Ein Gegner zaehlt dabei mit, sonst schwiegen die
    // Raketen ausgerechnet, wenn nur noch Gegner in der Naehe stehen.
    const reichweite = v.laserRange * 2.2;
    const feinde = this.hostileTargets(ctx, reichweite, 1);
    const list = this.targets(ctx, this.x, this.y, reichweite, feinde.length ? 0 : 1);
    if (!feinde.length && !list.length) { this.missileT = 0.1; return energy; }

    const spent = this.spend(ctx, energy, v.missileCost);
    this.missileT = 1 / (v.missileRate * spent.rate);

    const count = Math.round(v.missileCount);
    ctx.hooks.onFire('missile', count);
    for (let i = 0; i < count; i++) {
      const angle = this.angle + rnd(-0.9, 0.9);
      ctx.hooks.onMissile(this.x, this.y, angle, v.missileDamage * v.globalDamage, v.missileSplash);
    }
    return spent.energy;
  }

  private fireNova(dt: number, ctx: ShipContext): void {
    const v = ctx.stats.values;
    this.novaT -= dt;
    if (this.novaT > 0) return;
    this.novaT = 1 / v.novaRate;
    ctx.hooks.onFire('nova', 1);
    ctx.hooks.onNova(this.x, this.y, v.novaRadius, v.novaDamage * v.globalDamage);
  }

  /**
   * Die Alphawand — der Rand des Sektors.
   *
   * **Weich, keine Mauer.** Die Welt bleibt in jede Richtung berechenbar
   * (`WORLD-001`) — und sie **hoert hier nicht auf**: die Weltgenerierung liest
   * `fieldRadius` nirgends, hinter der Wand liegen dieselben Brocken wie davor.
   * Begrenzt wird allein der Flug, indem das Schiff gebremst und
   * zurueckgeschoben wird. Eine harte Kollision waere Schaden ohne Gegner und
   * widerspraeche „Reibung statt Bestrafung".
   */
  private applyWall(dt: number): void {
    const world = balancing().world;
    const dist = Math.hypot(this.x, this.y);
    const ueber = dist - world.fieldRadius;
    if (ueber <= 0) return;

    const staerke = clamp(ueber / world.wallBand, 0, 1);
    const push = world.wallPush * staerke * dt;
    this.vx -= (this.x / dist) * push;
    this.vy -= (this.y / dist) * push;
    // Zusaetzlich bremsen: sonst traegt der Schwung weit ueber die Wand hinaus.
    const brems = 1 - Math.min(0.9, staerke * 2.2 * dt);
    this.vx *= brems;
    this.vy *= brems;
  }

  /** Wie weit das Schiff in der Alphawand steckt — 0 aussen, 1 ganz drin. */
  wallDepth(): number {
    const world = balancing().world;
    const ueber = Math.hypot(this.x, this.y) - world.fieldRadius;
    return ueber <= 0 ? 0 : clamp(ueber / world.wallBand, 0, 1);
  }

  /**
   * Die Drohnen: ausschwärmen, je einen eigenen Brocken freigraben.
   *
   * Vier Schritte, bewusst getrennt: Bestand, Ziele, Flug, Bohren.
   *
   * **Der Anflug ist Wirkung, nicht Darstellung.** Gebohrt wird vor Ort, nicht
   * ab der Zielzuweisung — möglich, seit der Server die Drohnen selbst führt und
   * ihre Position damit bestimmt, statt sie zu erfahren (`NET-001`, FG-32). Die
   * Bewegung ist gleichförmig und damit schrittweiten-unabhängig: Client (60 Hz)
   * und Server (5 Hz) kommen zur selben Zeit an.
   */
  private updateDrones(dt: number, ctx: ShipContext): void {
    const v = ctx.stats.values;
    fitDrones(this.drones, Math.round(v.droneCount), this.x, this.y);
    this.assignDrones(ctx, v.droneRange);
    flyDrones(this.drones, dt, this.x, this.y);
    this.drillDrones(dt, ctx);
  }

  /**
   * Jeder Drohne ein eigenes Ziel.
   *
   * **Gefecht und Abbau schließen einander aus** (Issue #39): steht ein
   * erreichbarer Gegner, bekommt kein Brocken mehr einen Drohnenplatz. Deshalb
   * sind es hier zwei getrennte Fälle statt einer gemischten Liste.
   */
  private assignDrones(ctx: ShipContext, range: number): void {
    const v = ctx.stats.values;
    const feinde = ctx.hostiles
      ? ctx.hostiles.inRange(this.x, this.y, range, this.hostileNear)
      : NO_HOSTILES;
    this.claimed.clear();
    if (feinde.length) { this.assignToHostiles(feinde, v); return; }

    // **Eine** Abfrage für den ganzen Schwarm. Ihr Radius wächst mit dem
    // Ausschwärmen mit, statt gleich die ganze Leine abzufragen: im Orbit sind
    // die Drohnen 52 Einheiten weg, und ein Kreis über die volle Leine wäre
    // sechzehnmal so viel Feld für nichts.
    const leash = balancing().world.droneLeash;
    let spread = 0;
    for (const drone of this.drones) {
      spread = Math.max(spread, Math.hypot(drone.x - this.x, drone.y - this.y));
    }
    const found = ctx.world.queryCircle(this.x, this.y, Math.min(leash, spread + range), this.droneNear);

    // Erzfilter in einen wiederverwendeten Puffer, nicht in ein neues Array.
    const aus = ctx.ignoredOres;
    this.droneFree.length = 0;
    for (const rock of found) {
      if (!aus?.[rock.ore]) this.droneFree.push(rock);
    }
    assignDroneTargets(
      this.drones, this.droneFree, this.claimed, this.x, this.y, range, ctx.wishOre ?? '',
    );
  }

  /**
   * Die Drohnen auf die Gegner verteilen — **gebündelt**, wie der Fokuskanal.
   *
   * Der nächste Gegner bekommt so viele Drohnen, wie er braucht; erst der
   * Überschuss geht an den nächsten. Ein Platz je Gegner wäre gestreutes Feuer:
   * alle überleben lange, und alle schießen die ganze Zeit weiter.
   */
  private assignToHostiles(feinde: Hostile[], v: DerivedStats['values']): void {
    this.needForHostiles(feinde, enemyDamage(v.droneDamage * v.globalDamage));
    for (const drone of this.drones) {
      const gehalten = drone.targetId
        ? feinde.find((hostile) => hostile.id === drone.targetId)
        : undefined;
      if (gehalten && (this.hostileNeed.get(gehalten.id) ?? 0) > 0) {
        this.take(gehalten);
        drone.tx = gehalten.x; drone.ty = gehalten.y;
      } else {
        drone.targetId = '';
      }
    }
    for (const drone of this.drones) {
      if (drone.targetId) continue;
      const ziel = feinde.find((hostile) => !this.claimed.has(hostile.id));
      if (!ziel) break;
      this.take(ziel);
      drone.targetId = ziel.id;
      drone.tx = ziel.x; drone.ty = ziel.y;
    }
  }

  /**
   * Wie viele Drohnen ein Gegner **hoechstens** binden darf.
   *
   * Gedeckelt auf die Drohnenzahl, damit ein zaeher Gegner die Rechnung nicht
   * ins Absurde treibt; ein Gegner, der drei Schuss braucht, bekommt drei.
   */
  private needForHostiles(feinde: Hostile[], shot: number): void {
    this.hostileNeed.clear();
    for (const hostile of feinde) {
      this.hostileNeed.set(hostile.id, slotsFor(hostile.hp, shot, this.drones.length));
    }
  }

  /** Ein Ziel belegen: Brocken einmal, Gegner so oft, wie er noch braucht. */
  private take(ziel: { id: string }): void {
    const rest = this.hostileNeed.get(ziel.id);
    if (rest === undefined) { this.claimed.add(ziel.id); return; }
    if (rest <= 1) this.claimed.add(ziel.id);
    this.hostileNeed.set(ziel.id, rest - 1);
  }

  /**
   * Bohren — aber erst **vor Ort**.
   *
   * Das ist die Umkehr der alten Regel: gebohrt wurde ab der Zielzuweisung, weil
   * der Server die Anflugzeit nicht abbilden konnte. Er rechnet sie jetzt selbst.
   */
  private drillDrones(dt: number, ctx: ShipContext): void {
    const v = ctx.stats.values;
    const emptyRate = balancing().emptyRate;
    for (const drone of this.drones) {
      if (!drone.targetId) continue;
      drone.cooldown -= dt;
      if (drone.cooldown > 0) continue;
      const treffer = this.drillTarget(drone, ctx);
      if (treffer === 'weg') { drone.targetId = ''; continue; }
      if (treffer === 'unterwegs') continue;
      drone.cooldown = 1 / (v.droneRate * (this.empty ? emptyRate : 1));
    }
  }

  /**
   * Einmal bohren bzw. schiessen.
   *
   * Eine `e:`-ID kann `rockById` nie treffen — sie hat vier Teile, die Abfrage
   * erwartet drei. Die Reihenfolge ist trotzdem Gegner zuerst: es ist ein
   * Kartenzugriff, und die Absicht steht damit im Code.
   */
  private drillTarget(drone: Drone, ctx: ShipContext): 'weg' | 'unterwegs' | 'treffer' {
    const v = ctx.stats.values;
    const hostile = ctx.hostiles?.byId(drone.targetId);
    if (hostile && ctx.hostiles?.alive(hostile)) {
      if (!droneArrived(drone, hostile.r)) return 'unterwegs';
      ctx.hooks.onFire('drone', 1);
      ctx.hooks.onHostileDrone(drone, hostile, enemyDamage(v.droneDamage * v.globalDamage));
      return 'treffer';
    }
    const rock = ctx.world.rockById(drone.targetId);
    if (!rock) return 'weg';
    if (!droneArrived(drone, rock.r)) return 'unterwegs';
    ctx.hooks.onFire('drone', 1);
    ctx.hooks.onDrone(drone, rock, v.droneDamage * v.globalDamage);
    return 'treffer';
  }

}
